import { inject, Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState, getHealthSummary } from '../../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../../user/store/selectors/user.selectors';
import { PatientFamilyHistoriesService } from '../../../services/patient-family-histories/patient-family-histories.service';
import {
  getAllPatientFamilyHistories,
  getAllPatientFamilyHistoriesFailure,
  getAllPatientFamilyHistoriesSuccess,
  getPatientFamilyHistories,
  getPatientFamilyHistoriesFailure,
  getPatientFamilyHistoriesSuccess,
  recordPatientFamilyHistoryPrintEvent,
  recordPatientFamilyHistoryPrintEventFailure,
  recordPatientFamilyHistoryPrintEventSuccess,
  setPageNumber,
} from '../actions/patient-family-history.actions';
import {
  selectCacheLoadedAt,
  selectCachePatientId,
  selectCurrentCount,
  selectIsAtEndOfData,
  selectIsFullDatasetLoaded,
  selectPageNumber,
  selectPageSize,
  selectPatientFamilyHistories,
} from '../selectors/patient-family-history.selector';

@Injectable()
export class PatientFamilyHistoryEffects {
  private readonly HEALTH_SUMMARY_WIDGET_PAGE_SIZE = 5;
  private readonly FULL_DATASET_CACHE_TTL_MS = 5 * 60 * 1000;
  private actions$ = inject(Actions);
  private patientFamilyHistoriesService = inject(PatientFamilyHistoriesService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientFamilyHistories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientFamilyHistories, setPageNumber, getHealthSummary),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
        this.store.select(selectCurrentCount),
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectIsAtEndOfData),
        this.store.select(selectPatientFamilyHistories),
      ]),
      mergeMap(
        ([
          action,
          patientId,
          pageNumber,
          pageSize,
          currentCount,
          isFullDatasetLoaded,
          cachePatientId,
          cacheLoadedAt,
          isAtEndOfData,
          cachedPatientFamilyHistories,
        ]) => {
          const shouldUseHealthSummaryCache = action.type === getHealthSummary.type && currentCount > 0;
          const requestPageSize =
            action.type === getHealthSummary.type ? this.HEALTH_SUMMARY_WIDGET_PAGE_SIZE : pageSize;
          if (shouldUseHealthSummaryCache) {
            if (isDevMode()) {
              console.debug('[Family History][Cache] Health summary cache hit; skipping API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientFamilyHistoriesSuccess({
                patientFamilyHistories: cachedPatientFamilyHistories,
                pageNumber: pageNumber || 1,
                isAtEndOfData: isAtEndOfData ?? false,
                totalRecordCount: currentCount,
              })
            );
          }

          const shouldUseFullDatasetCache =
            patientId != null &&
            isFullDatasetLoaded &&
            cachePatientId === patientId &&
            this.isCacheFresh(cacheLoadedAt);
          if (shouldUseFullDatasetCache) {
            if (isDevMode()) {
              console.debug('[Family History][Cache] Full-dataset cache hit; skipping paged API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientFamilyHistoriesSuccess({
                patientFamilyHistories: cachedPatientFamilyHistories,
                pageNumber: 1,
                isAtEndOfData: true,
                totalRecordCount: currentCount,
              })
            );
          }

          return this.patientFamilyHistoriesService
            .postPatientFamilyHistories(patientId, pageNumber, requestPageSize)
            .pipe(
              takeUntil(this.cancelRequest$),
              map(response => {
                let isAtEndOfData = false;
                const totalRecordCount = response.totalRecordCount;
                let currentPage = pageNumber || 1;
                let patientFamilyHistories = response.result;

                if (patientFamilyHistories == null || patientFamilyHistories.length == 0) {
                  patientFamilyHistories = [];
                  currentPage--;
                  isAtEndOfData = true;
                } else if (currentCount + patientFamilyHistories.length === totalRecordCount) {
                  isAtEndOfData = true;
                }

                return getPatientFamilyHistoriesSuccess({
                  patientFamilyHistories,
                  pageNumber: currentPage <= 0 ? 1 : currentPage,
                  isAtEndOfData,
                  totalRecordCount,
                });
              }),
              catchError(err => {
                return of(getPatientFamilyHistoriesFailure({ error: err }));
              })
            );
        }
      )
    );
  });

  getAllPatientFamilyHistories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllPatientFamilyHistories),
      concatLatestFrom(() => [
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientFamilyHistories),
      ]),
      mergeMap(([{ patientId }, isFullDatasetLoaded, cachePatientId, cacheLoadedAt, patientFamilyHistories]) => {
        if (isFullDatasetLoaded && cachePatientId === patientId && this.isCacheFresh(cacheLoadedAt)) {
          if (isDevMode()) {
            console.debug('[Family History][Cache] Full-dataset cache hit; skipping GetAllByPatientId call.', {
              patientId,
              count: patientFamilyHistories.length,
            });
          }
          return of(
            getAllPatientFamilyHistoriesSuccess({
              patientFamilyHistories,
              patientId,
              loadedAt: cacheLoadedAt ?? Date.now(),
            })
          );
        }

        return this.patientFamilyHistoriesService.getAllPatientFamilyHistoriesByPatientId(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientFamilyHistories =>
            getAllPatientFamilyHistoriesSuccess({
              patientFamilyHistories,
              patientId,
              loadedAt: Date.now(),
            })
          ),
          catchError(err => of(getAllPatientFamilyHistoriesFailure({ error: err })))
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientFamilyHistoryPrintEvent),
      mergeMap(({ patientId, patientFamilyHistoryIds }) =>
        this.patientFamilyHistoriesService.postPrintEvent(patientId, patientFamilyHistoryIds).pipe(
          map(() => recordPatientFamilyHistoryPrintEventSuccess()),
          catchError(err => of(recordPatientFamilyHistoryPrintEventFailure({ error: err })))
        )
      )
    );
  });

  private isCacheFresh(cacheLoadedAt: number | null): boolean {
    if (!cacheLoadedAt) {
      return false;
    }

    return Date.now() - cacheLoadedAt <= this.FULL_DATASET_CACHE_TTL_MS;
  }
}
